import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { propertyService } from '../../services/propertyService';
import { policyService } from '../../services/policyService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent } from '../../components/ui/Card';
import LocationMapPicker from '../../components/common/LocationMapPicker';
import { AddressAutocompleteField } from '../../components/common/AddressAutocompleteField';
import { useAuthStore } from '../../stores/authStore';
import { Building2 } from 'lucide-react';
import type { AxiosError } from 'axios';

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Array<{ field: string; message: string }>;
  };
  message?: string;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormState {
  // Step 1 — Basic info + Classification
  name: string;
  description: string;
  categoryId: string;
  typeId: string;
  spaceTypeId: string;
  // Step 2 — Location
  contactPhone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  // Step 3 — Details
  maxGuests: number;
  checkInTime: string;
  checkOutTime: string;
  minStay: number;
  instantBooking: boolean;
  yearBuilt: string;
  totalFloors: string;
  // Step 4 — Pricing & Policies
  basePrice: string;
  currency: string;
  cleaningFee: string;
  securityDeposit: string;
  cancellationPolicy: string;
  smokingAllowed: boolean;
  petsAllowed: boolean;
  partiesAllowed: boolean;
  // Step 5 — Photos & Tags
  experienceTagIds: string[];
}

const INITIAL_FORM: FormState = {
  name: '', description: '',
  categoryId: '', typeId: '', spaceTypeId: '',
  contactPhone: '', addressLine1: '', addressLine2: '', city: '', state: '', country: '', postalCode: '',
  latitude: null, longitude: null,
  maxGuests: 2, checkInTime: '14:00', checkOutTime: '11:00', minStay: 1,
  instantBooking: false, yearBuilt: '', totalFloors: '',
  basePrice: '', currency: 'INR', cleaningFee: '', securityDeposit: '',
  cancellationPolicy: 'FLEXIBLE',
  smokingAllowed: false, petsAllowed: false, partiesAllowed: false,
  experienceTagIds: [],
};

const CURRENCIES = [
  { label: 'INR (₹)', value: 'INR' },
  { label: 'USD ($)', value: 'USD' },
  { label: 'EUR (€)', value: 'EUR' },
  { label: 'GBP (£)', value: 'GBP' },
];

const CANCELLATION_OPTIONS = [
  { value: 'FLEXIBLE', label: 'Flexible', desc: 'Full refund up to 24h before check-in' },
  { value: 'MODERATE', label: 'Moderate', desc: '50% refund if cancelled 7+ days before' },
  { value: 'STRICT', label: 'Strict', desc: '50% refund if cancelled 14+ days before' },
  { value: 'NON_REFUNDABLE', label: 'Non-refundable', desc: 'No refunds after booking' },
];

const TOTAL_STEPS = 5;

// ─── Step progress bar ────────────────────────────────────────────────────────

function StepBar({ current }: { current: number }) {
  const labels = ['Basics', 'Location', 'Details', 'Pricing', 'Photos'];
  return (
    <div className="mt-4 flex items-center gap-0">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                done ? 'bg-primary-500 text-white' :
                active ? 'bg-primary-600 text-white ring-4 ring-primary-100' :
                'bg-gray-100 text-gray-400'
              }`}>
                {done ? '✓' : step}
              </div>
              <span className={`mt-1 hidden text-xs sm:block ${active ? 'font-semibold text-primary-600' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className={`mb-4 h-0.5 flex-1 transition-colors ${step < current ? 'bg-primary-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Tile selector ────────────────────────────────────────────────────────────

function TileSelect<T extends { id: string; name: string; description?: string | null }>({
  items, selected, onSelect, columns = 3,
}: {
  items: T[];
  selected: string;
  onSelect: (id: string) => void;
  columns?: 2 | 3 | 4;
}) {
  const colClass = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' }[columns];
  return (
    <div className={`grid grid-cols-2 ${colClass} gap-3`}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          className={`rounded-xl border-2 p-3 text-left transition-all ${
            selected === item.id
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50'
          }`}
        >
          <p className={`text-sm font-semibold ${selected === item.id ? 'text-primary-700' : 'text-gray-800'}`}>
            {item.name}
          </p>
          {item.description && (
            <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{item.description}</p>
          )}
        </button>
      ))}
    </div>
  );
}

function Toggle({ label, icon, value, onChange }: {
  label: string; icon: string; value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-all ${
        value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
      <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${value ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-400'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    </button>
  );
}

/** Reusable red asterisk for required field labels */
function Req() {
  return <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>;
}

function TagPill({ label, selected, onToggle }: { label: string; selected: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
        selected ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Field → step mapping (for auto-navigation on error) ─────────────────────

const FIELD_TO_STEP: Record<string, number> = {
  name: 1, description: 1, categoryId: 1, typeId: 1, spaceTypeId: 1,
  contactPhone: 2, addressLine1: 2, addressLine2: 2, city: 2, state: 2, country: 2, postalCode: 2, latitude: 2, longitude: 2,
  maxGuests: 3, checkInTime: 3, checkOutTime: 3, minStay: 3, maxStay: 3, instantBooking: 3,
  yearBuilt: 3, totalFloors: 3,
  basePrice: 4, currency: 4, cleaningFee: 4, securityDeposit: 4,
  smokingAllowed: 4, petsAllowed: 4, partiesAllowed: 4, cancellationPolicy: 4,
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Property name', description: 'Description',
  categoryId: 'Category', typeId: 'Property type', spaceTypeId: 'Space type',
  contactPhone: 'Contact phone', addressLine1: 'Street address', addressLine2: 'Address line 2',
  city: 'City', state: 'State', country: 'Country', postalCode: 'Postal code',
  latitude: 'Latitude', longitude: 'Longitude',
  maxGuests: 'Max guests', checkInTime: 'Check-in time',
  checkOutTime: 'Check-out time', minStay: 'Minimum stay nights',
  instantBooking: 'Instant booking', yearBuilt: 'Year built', totalFloors: 'Total floors',
  basePrice: 'Price per night', currency: 'Currency',
  cleaningFee: 'Cleaning fee', securityDeposit: 'Security deposit',
  smokingAllowed: 'Smoking rule', petsAllowed: 'Pets rule', partiesAllowed: 'Parties rule',
};

function parseApiError(err: AxiosError<ApiErrorBody>): { message: string; step?: number } {
  const apiError = err.response?.data?.error;

  // Network / unknown error
  if (!apiError) {
    return { message: err.message || 'Network error — please check your connection and try again.' };
  }

  // Zod validation errors — show each field with a human-readable label
  if (apiError.code === 'VALIDATION_ERROR' && Array.isArray(apiError.details) && apiError.details.length > 0) {
    const lines = apiError.details.map((d: { field: string; message: string }) => {
      const label = FIELD_LABELS[d.field] ?? d.field;
      return `• ${label}: ${d.message}`;
    });
    const firstField = apiError.details[0]?.field;
    const step = FIELD_TO_STEP[firstField];
    return {
      message: 'Please fix the following before continuing:\n' + lines.join('\n'),
      step,
    };
  }

  // Auth / other well-known codes
  const codeMessages: Record<string, string> = {
    UNAUTHORIZED: 'Your session has expired — please sign in again.',
    FORBIDDEN: 'You do not have permission to create properties.',
    CONFLICT: apiError.message ?? 'A property with this name already exists.',
    NOT_FOUND: 'The requested resource was not found. Please refresh and try again.',
    RATE_LIMITED: 'Too many requests — please wait a moment and try again.',
    INTERNAL_ERROR: 'A server error occurred. Please try again in a few minutes.',
  };
  const known = apiError.code ? codeMessages[apiError.code] : undefined;
  if (known) return { message: known };

  // HTTP status fallbacks (when code is missing but status is available)
  if (!apiError.code) {
    const status = err.response?.status ?? 0;
    if (status === 401) return { message: 'Your session has expired — please sign in again.' };
    if (status === 403) return { message: 'You do not have permission to perform this action.' };
    if (status === 413) return { message: 'One or more files are too large. Maximum allowed size is 10 MB.' };
    if (status >= 500) return { message: 'A server error occurred. Please try again in a few minutes.' };
  }

  return { message: apiError.message ?? 'Something went wrong. Please try again.' };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CreatePropertyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();

  // PLATFORM_STAFF / PLATFORM_ADMIN / SUPER_ADMIN creating on behalf of an org
  const role = user?.roles?.[0];
  const isPlatformStaff = role === 'PLATFORM_STAFF' || role === 'PLATFORM_ADMIN' || role === 'SUPER_ADMIN';
  const orgIdFromUrl = searchParams.get('organizationId') ?? '';
  const orgNameFromUrl = searchParams.get('orgName') ?? '';
  // Each fixed-tier role has its own Organizations detail route -- redirect back to whichever one
  // matches the current user so the post-create toast/back-nav lands somewhere they can access.
  const orgDetailBasePath =
    role === 'SUPER_ADMIN' ? '/dashboard/super-admin/organizations'
    : role === 'PLATFORM_ADMIN' ? '/dashboard/platform-admin/organizations'
    : '/dashboard/platform-staff/organizations';

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [photos, setPhotos] = useState<{ file: File; preview: string; uploading: boolean; error: boolean }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [photoErrors, setPhotoErrors] = useState<string[]>([]);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const set = (field: keyof FormState) => (val: FormState[typeof field]) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  const fieldChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      set(field)(e.target.value as never);

  // ── Data fetches ──────────────────────────────────────────────────────────

  const { data: categories = [], isLoading: loadingCats } = useQuery({
    queryKey: ['property-categories'],
    queryFn: propertyService.getCategories,
    staleTime: 10 * 60_000,
  });

  const { data: allTypes = [], isLoading: loadingTypes } = useQuery({
    queryKey: ['property-types'],
    queryFn: () => propertyService.getTypes(),
    staleTime: 10 * 60_000,
  });

  const { data: spaceTypes = [], isLoading: loadingSpaceTypes } = useQuery({
    queryKey: ['space-types'],
    queryFn: propertyService.getSpaceTypes,
    staleTime: 10 * 60_000,
  });

  const { data: experienceTagGroups = {}, isLoading: loadingTags } = useQuery({
    queryKey: ['experience-tags'],
    queryFn: propertyService.getExperienceTags,
    staleTime: 10 * 60_000,
  });

  const filteredTypes = form.categoryId
    ? allTypes.filter((t) => t.categoryId === form.categoryId)
    : allTypes;

  // ── Photo handling ────────────────────────────────────────────────────────

  const addPhotos = (files: File[]) => {
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      error: false,
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadPhotos = async (propertyId: string) => {
    const errs: string[] = [];
    for (let i = 0; i < photos.length; i++) {
      setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: true } : p));
      try {
        await propertyService.uploadPhoto(propertyId, photos[i].file);
        setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: false } : p));
      } catch (err) {
        setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: false, error: true } : p));
        const name = photos[i].file.name;
        const status = (err as AxiosError).response?.status;
        if (status === 400) {
          errs.push(`"${name}": File type or size is not supported (JPEG, PNG, WebP up to 10 MB only).`);
        } else if (status === 413) {
          errs.push(`"${name}": File is too large. Maximum allowed size is 10 MB.`);
        } else {
          errs.push(`"${name}": Upload failed — please try again.`);
        }
      }
    }
    if (errs.length > 0) setPhotoErrors(errs);
  };

  // ── Final submit ──────────────────────────────────────────────────────────

  const handleFinalSubmit = async () => {
    if (!form.name.trim()) { setSubmitError('Property name is required.'); return; }
    if (!form.city.trim()) { setSubmitError('City is required.'); setStep(2); return; }
    if (!form.country.trim()) { setSubmitError('Country is required.'); setStep(2); return; }
    if (isPlatformStaff && !orgIdFromUrl) {
      setSubmitError('Please select an organization first. Go back and choose an organization from the Organizations page.');
      return;
    }
    setSubmitError('');
    setPhotoErrors([]);
    setIsSubmitting(true);
    try {
      // Build a clean payload — ensure booleans are actual booleans (not truthy/falsy strings)
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        categoryId: form.categoryId,
        typeId: form.typeId,
        spaceTypeId: form.spaceTypeId || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        addressLine1: (form.addressLine1.trim() || form.city.trim()),
        addressLine2: form.addressLine2.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim() || undefined,
        country: form.country.trim(),
        postalCode: form.postalCode.trim() || undefined,
        latitude: form.latitude ?? undefined,
        longitude: form.longitude ?? undefined,
        maxGuests: Number(form.maxGuests),
        checkInTime: form.checkInTime || undefined,
        checkOutTime: form.checkOutTime || undefined,
        minStay: Number(form.minStay) || 1,
        instantBooking: Boolean(form.instantBooking),
        yearBuilt: form.yearBuilt ? Number(form.yearBuilt) : undefined,
        totalFloors: form.totalFloors ? Number(form.totalFloors) : undefined,
        smokingAllowed: Boolean(form.smokingAllowed),
        petsAllowed: Boolean(form.petsAllowed),
        partiesAllowed: Boolean(form.partiesAllowed),
        // Required when PLATFORM_STAFF/ADMIN creates on behalf of an org
        ...(isPlatformStaff && orgIdFromUrl ? { organizationId: orgIdFromUrl } : {}),
      };

      // 1. Create the property
      const res = await propertyService.create(payload);

      const property = res.data.data;
      if (!property?.id) throw new Error('Failed to create property — no ID returned');
      const propertyId = property.id;

      // 2. Create pricing
      if (form.basePrice && Number(form.basePrice) > 0) {
        await propertyService.createPricing(propertyId, {
          priceType: 'NIGHTLY',
          basePrice: Number(form.basePrice),
          currency: form.currency,
          ...(form.cleaningFee ? { cleaningFee: Number(form.cleaningFee) } : {}),
          ...(form.securityDeposit ? { securityDeposit: Number(form.securityDeposit) } : {}),
        });
      }

      // 3. Create cancellation policy
      await policyService.create(propertyId, {
        type: 'CANCELLATION',
        title: `${form.cancellationPolicy.charAt(0)}${form.cancellationPolicy.slice(1).toLowerCase().replace(/_/g, ' ')} cancellation`,
        content: CANCELLATION_OPTIONS.find(o => o.value === form.cancellationPolicy)?.desc ?? '',
        cancellationType: form.cancellationPolicy,
      });

      // 4. Set experience tags
      if (form.experienceTagIds.length > 0) {
        await propertyService.setExperienceTags(propertyId, form.experienceTagIds);
      }

      // 5. Upload photos
      if (photos.length > 0) {
        await uploadPhotos(propertyId);
      }

      if (isPlatformStaff && orgIdFromUrl) {
        navigate(
          `${orgDetailBasePath}/${orgIdFromUrl}?propertyCreated=1&propertyName=${encodeURIComponent(form.name)}`,
        );
      } else {
        navigate(`/dashboard/properties/${propertyId}/edit`);
      }
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorBody>;
      console.error('[CreateProperty] API error:', axiosErr.response?.data ?? axiosErr.message);
      const { message, step: errorStep } = parseApiError(axiosErr);
      setSubmitError(message);
      // Auto-navigate to the step that has the failing field
      if (errorStep && errorStep < 5) {
        setStep(errorStep);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step validation ───────────────────────────────────────────────────────

  const isStep1Valid = form.name.trim().length >= 2 && !!form.categoryId && !!form.typeId && !!form.spaceTypeId;
  const isStep2Valid = !!form.addressLine1.trim() && !!form.city.trim() && !!form.country.trim();
  const isStep3Valid = form.maxGuests >= 1;
  const isStep4Valid = !!form.basePrice && Number(form.basePrice) > 0 && !!form.currency;

  // ── Render ────────────────────────────────────────────────────────────────

  // PLATFORM_STAFF with no org selected → block the form and send them back
  if (isPlatformStaff && !orgIdFromUrl) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <Building2 size={40} className="mx-auto mb-4 text-gray-300" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">No organization selected</h2>
        <p className="text-sm text-gray-500 mb-6">
          You must choose an organization before adding a property. Go to the Organizations page,
          find the organization, and click <strong>Add Property</strong>.
        </p>
        <Button onClick={() => navigate(orgDetailBasePath)}>
          Go to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">List your property</h1>
        <p className="mt-1 text-sm text-gray-500">Step {step} of {TOTAL_STEPS}</p>
        <StepBar current={step} />
      </div>

      {/* ── Org context banner (shown when staff is creating on behalf of an org) */}
      {isPlatformStaff && orgIdFromUrl && (
        <div className="mb-5 flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <Building2 size={18} className="text-primary-600 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-primary-800">
              Adding property on behalf of <span className="font-semibold">{orgNameFromUrl || 'selected organization'}</span>
            </p>
            <p className="text-xs text-primary-600 mt-0.5">
              This property will be owned by the organization admin.{' '}
              <button
                type="button"
                onClick={() => navigate(orgDetailBasePath)}
                className="underline hover:no-underline"
              >
                Change organization
              </button>
            </p>
          </div>
        </div>
      )}

      {/* ── Global error banner (visible on any step) ─────────────────────── */}
      {submitError && (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {submitError.split('\n').map((line, i) => (
            <p key={i} className={i === 0 ? 'font-semibold' : 'mt-1 ml-2'}>{line}</p>
          ))}
        </div>
      )}

      {/* ── Step 1: Basics + Classification ───────────────────────────────── */}
      {step === 1 && (
        <Card>
          <CardContent className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Tell us about your place</h2>
              <p className="mt-1 text-sm text-gray-500">Start with a name and what kind of place it is.</p>
            </div>

            {/* Name & Description */}
            <div className="space-y-4">
              <Input
                label="Property name *"
                value={form.name}
                onChange={fieldChange('name')}
                placeholder="e.g. Sunrise PG Rooms, Coastal Co-living"
                required
              />
              <div className="space-y-1">
                <label htmlFor="property-description" className="block text-sm font-medium text-gray-700">
                  Description <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="property-description"
                  value={form.description}
                  onChange={fieldChange('description') as React.ChangeEventHandler<HTMLTextAreaElement>}
                  rows={3}
                  placeholder="Describe what makes your place special…"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2" role="group" aria-labelledby="category-label">
              <span id="category-label" className="block text-sm font-semibold text-gray-700">Category<Req /></span>
              {loadingCats ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {[1,2,3,4,5,6,7].map(i => <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
                </div>
              ) : (
                <TileSelect
                  items={categories}
                  selected={form.categoryId}
                  onSelect={(id) => { set('categoryId')(id); set('typeId')(''); }}
                  columns={3}
                />
              )}
            </div>

            {/* Type */}
            {form.categoryId && (
              <div className="space-y-2" role="group" aria-labelledby="property-type-label">
                <span id="property-type-label" className="block text-sm font-semibold text-gray-700">Property type<Req /></span>
                {loadingTypes ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {[1,2,3].map(i => <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
                  </div>
                ) : filteredTypes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No types for this category.</p>
                ) : (
                  <TileSelect
                    items={filteredTypes}
                    selected={form.typeId}
                    onSelect={set('typeId')}
                    columns={3}
                  />
                )}
              </div>
            )}

            {/* Space type */}
            {form.typeId && (
              <div className="space-y-2" role="group" aria-labelledby="space-type-label">
                <span id="space-type-label" className="block text-sm font-semibold text-gray-700">What will guests have?<Req /></span>
                {loadingSpaceTypes ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[1,2,3,4].map(i => <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
                  </div>
                ) : (
                  <TileSelect
                    items={spaceTypes}
                    selected={form.spaceTypeId}
                    onSelect={set('spaceTypeId')}
                    columns={2}
                  />
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={() => setStep(2)} disabled={!isStep1Valid}>Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Location ───────────────────────────────────────────────── */}
      {step === 2 && (
        <>
          <Card>
            <CardContent className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Where is it located?</h2>
                <p className="mt-1 text-sm text-gray-500">Full address and GPS coordinates are only shared with confirmed guests.</p>
              </div>

              {/* Contact phone */}
              <Input
                label="Contact phone (shown to guests after booking)"
                value={form.contactPhone}
                onChange={fieldChange('contactPhone')}
                placeholder="+91 98765 43210"
                type="tel"
              />

              {/* Places-Autocomplete-first: pick a place and every field below auto-fills */}
              <div className="space-y-1.5">
                {/* Not a <label> -- AddressAutocompleteField's underlying input already carries
                    its own aria-label; a sibling <label> with no htmlFor would be an
                    unassociated duplicate accessible name, not a real fix. */}
                <p className="text-sm font-medium text-gray-700">Search for the address</p>
                <AddressAutocompleteField
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  onSelect={(addr) => setForm((prev) => ({
                    ...prev,
                    addressLine1: addr.addressLine1 || prev.addressLine1,
                    city: addr.city || prev.city,
                    state: addr.state || prev.state,
                    country: addr.country || prev.country,
                    postalCode: addr.postalCode || prev.postalCode,
                    latitude: addr.lat,
                    longitude: addr.lng,
                  }))}
                />
                <p className="text-xs text-gray-400">Or fill in the fields below manually.</p>
              </div>

              {/* Address */}
              <Input label="Street address *" value={form.addressLine1} onChange={fieldChange('addressLine1')} placeholder="123 Main Street" required />
              <Input label="Address line 2" value={form.addressLine2} onChange={fieldChange('addressLine2')} placeholder="Flat, floor, building (optional)" />
              <div className="grid grid-cols-2 gap-4">
                <Input label="City *" value={form.city} onChange={fieldChange('city')} required placeholder="Mumbai" />
                <Input label="State / Province" value={form.state} onChange={fieldChange('state')} placeholder="Maharashtra" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Country *" value={form.country} onChange={fieldChange('country')} required placeholder="India" />
                <Input label="Postal code" value={form.postalCode} onChange={fieldChange('postalCode')} placeholder="400001" />
              </div>

              {/* GPS coordinates */}
              <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">GPS coordinates <span className="font-normal text-gray-400">(optional but recommended)</span></p>
                    <p className="text-xs text-gray-500 mt-0.5">Helps guests find you on the map after booking</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 transition-colors"
                  >
                    <span>🗺</span>
                    <span>{form.latitude != null ? 'Change on map' : 'Pick on map'}</span>
                  </button>
                </div>

                {form.latitude != null && form.longitude != null ? (
                  <div className="flex items-center gap-3 rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <span className="text-lg" aria-hidden="true">📍</span>
                    <div className="text-sm">
                      <span className="text-gray-600">Lat: </span>
                      <span className="font-mono font-medium text-gray-900">{form.latitude.toFixed(6)}</span>
                      <span className="mx-2 text-gray-300">|</span>
                      <span className="text-gray-600">Lng: </span>
                      <span className="font-mono font-medium text-gray-900">{form.longitude.toFixed(6)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { set('latitude')(null); set('longitude')(null); }}
                      aria-label="Clear GPS coordinates"
                      className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>No location pinned yet</span>
                  </div>
                )}

                {/* Manual lat/lng entry */}
                <details>
                  <summary className="cursor-pointer text-xs text-primary-600 hover:text-primary-700">Enter coordinates manually</summary>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label htmlFor="property-latitude" className="block text-xs font-medium text-gray-600">Latitude (−90 to 90)</label>
                      <input
                        id="property-latitude"
                        type="number"
                        step="any"
                        min="-90"
                        max="90"
                        value={form.latitude ?? ''}
                        onChange={(e) => set('latitude')(e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="e.g. 19.0760"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="property-longitude" className="block text-xs font-medium text-gray-600">Longitude (−180 to 180)</label>
                      <input
                        id="property-longitude"
                        type="number"
                        step="any"
                        min="-180"
                        max="180"
                        value={form.longitude ?? ''}
                        onChange={(e) => set('longitude')(e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="e.g. 72.8777"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </details>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
                <Button onClick={() => setStep(3)} disabled={!isStep2Valid}>Next →</Button>
              </div>
            </CardContent>
          </Card>

          {/* Map picker modal */}
          {showMapPicker && (
            <LocationMapPicker
              lat={form.latitude}
              lng={form.longitude}
              onConfirm={(lat, lng) => {
                set('latitude')(lat);
                set('longitude')(lng);
                setShowMapPicker(false);
              }}
              onClose={() => setShowMapPicker(false)}
            />
          )}
        </>
      )}

      {/* ── Step 3: Details ────────────────────────────────────────────────── */}
      {step === 3 && (
        <Card>
          <CardContent className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">About your place</h2>
              <p className="mt-1 text-sm text-gray-500">Help guests plan their stay.</p>
            </div>

            {/* Guests & Stay */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <span id="max-guests-label" className="block text-sm font-semibold text-gray-700">Max guests<Req /></span>
                <div className="flex items-center gap-3" role="group" aria-labelledby="max-guests-label">
                  <button type="button" onClick={() => set('maxGuests')(Math.max(1, form.maxGuests - 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">−</button>
                  <span className="w-8 text-center text-base font-semibold">{form.maxGuests}</span>
                  <button type="button" onClick={() => set('maxGuests')(Math.min(50, form.maxGuests + 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">+</button>
                </div>
              </div>
              <div className="space-y-1">
                <span id="min-stay-label" className="block text-sm font-semibold text-gray-700">Min stay (nights)</span>
                <div className="flex items-center gap-3" role="group" aria-labelledby="min-stay-label">
                  <button type="button" onClick={() => set('minStay')(Math.max(1, form.minStay - 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">−</button>
                  <span className="w-8 text-center text-base font-semibold">{form.minStay}</span>
                  <button type="button" onClick={() => set('minStay')(form.minStay + 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">+</button>
                </div>
              </div>
            </div>

            {/* Check-in / Check-out */}
            <div className="grid grid-cols-2 gap-4">
              <Input label="Check-in time" type="time" value={form.checkInTime} onChange={fieldChange('checkInTime')} />
              <Input label="Check-out time" type="time" value={form.checkOutTime} onChange={fieldChange('checkOutTime')} />
            </div>

            {/* Instant booking */}
            <div className="flex items-center justify-between rounded-xl border border-gray-200 p-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">Instant booking</p>
                <p className="text-xs text-gray-500">Guests can book without your approval</p>
              </div>
              <button type="button" onClick={() => set('instantBooking')(!form.instantBooking)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${form.instantBooking ? 'bg-primary-500' : 'bg-gray-200'}`}>
                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${form.instantBooking ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-primary-600 hover:text-primary-700">+ More details (optional)</summary>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <Input label="Year built" type="number" value={form.yearBuilt} onChange={fieldChange('yearBuilt')} placeholder="e.g. 2010" />
                <Input label="Total floors" type="number" value={form.totalFloors} onChange={fieldChange('totalFloors')} placeholder="e.g. 4" />
              </div>
            </details>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
              <Button onClick={() => setStep(4)} disabled={!isStep3Valid}>Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 4: Pricing & Policies ─────────────────────────────────────── */}
      {step === 4 && (
        <Card>
          <CardContent className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pricing &amp; policies</h2>
              <p className="mt-1 text-sm text-gray-500">Set your rates and house rules.</p>
            </div>

            {/* Pricing */}
            <div className="space-y-3">
              <div className="flex items-center gap-1">
                <h3 className="text-sm font-semibold text-gray-700">Pricing</h3>
                <Req />
                <span className="text-xs text-gray-400 ml-1">(required before review)</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Price per night *" type="number" min="0" value={form.basePrice} onChange={fieldChange('basePrice')} placeholder="0.00" required />
                <div className="space-y-1">
                  <label htmlFor="property-currency" className="block text-sm font-medium text-gray-700">Currency<Req /></label>
                  <select id="property-currency" value={form.currency} onChange={fieldChange('currency')}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                    {CURRENCIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Cleaning fee" type="number" min="0" value={form.cleaningFee} onChange={fieldChange('cleaningFee')} placeholder="Optional" />
                <Input label="Security deposit" type="number" min="0" value={form.securityDeposit} onChange={fieldChange('securityDeposit')} placeholder="Optional" />
              </div>
            </div>

            {/* Cancellation policy */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Cancellation policy *</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {CANCELLATION_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button" onClick={() => set('cancellationPolicy')(opt.value)}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${form.cancellationPolicy === opt.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-primary-300'}`}>
                    <p className={`text-sm font-semibold ${form.cancellationPolicy === opt.value ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* House rules */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">House rules</h3>
              <div className="flex flex-col gap-2">
                <Toggle label="Smoking" icon="🚬" value={form.smokingAllowed} onChange={set('smokingAllowed')} />
                <Toggle label="Pets" icon="🐾" value={form.petsAllowed} onChange={set('petsAllowed')} />
                <Toggle label="Parties &amp; events" icon="🎉" value={form.partiesAllowed} onChange={set('partiesAllowed')} />
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(3)}>← Back</Button>
              <Button onClick={() => setStep(5)} disabled={!isStep4Valid}>Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 5: Photos & Tags ──────────────────────────────────────────── */}
      {step === 5 && (
        <Card>
          <CardContent className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Photos &amp; discoverability
                <span className="ml-1.5 text-sm font-normal text-red-500">(at least 1 photo required for review)</span>
              </h2>
              <p className="mt-1 text-sm text-gray-500">Great photos get 3× more bookings. Upload at least one — you can add more after.</p>
            </div>

            {/* Photo upload */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-video overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                    <img src={p.preview} alt="" className="h-full w-full object-cover" />
                    {p.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      </div>
                    )}
                    {p.error && <div className="absolute bottom-0 left-0 right-0 bg-red-500 py-1 text-center text-xs text-white">Failed</div>}
                    {i === 0 && !p.uploading && (
                      <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">Cover</span>
                    )}
                    <button type="button" onClick={() => removePhoto(i)}
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80">×</button>
                  </div>
                ))}
                <label className="flex aspect-video cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 transition-colors hover:border-primary-400 hover:text-primary-500">
                  <span className="text-2xl">+</span>
                  <span className="text-xs font-medium">Add photo</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="sr-only"
                    onChange={(e) => e.target.files && addPhotos(Array.from(e.target.files))} />
                </label>
              </div>
            </div>

            {/* Experience tags */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">
                What&apos;s special about your place? <span className="font-normal text-gray-400">(optional)</span>
              </h3>
              {loadingTags ? (
                <div className="flex flex-wrap gap-2">{[1,2,3,4,5,6].map(i => <div key={i} className="h-8 w-24 animate-pulse rounded-full bg-gray-100" />)}</div>
              ) : (
                Object.entries(experienceTagGroups).map(([group, tags]) => (
                  <div key={group}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{group.replace('_', ' ')}</p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <TagPill key={tag.id} label={tag.name} selected={form.experienceTagIds.includes(tag.id)}
                          onToggle={() => {
                            const ids = form.experienceTagIds.includes(tag.id)
                              ? form.experienceTagIds.filter((id) => id !== tag.id)
                              : [...form.experienceTagIds, tag.id];
                            set('experienceTagIds')(ids);
                          }} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {photoErrors.length > 0 && (
              <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <p className="font-semibold">Some photos could not be uploaded:</p>
                {photoErrors.map((msg, i) => <p key={i} className="mt-1 ml-2">{msg}</p>)}
                <p className="mt-2 text-xs text-amber-600">Your property was created — you can retry uploading photos from the property edit page.</p>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(4)}>← Back</Button>
              <Button onClick={handleFinalSubmit} loading={isSubmitting}>
                {isSubmitting ? 'Creating…' : 'Create property'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
